"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

interface Props {
	image: string;
	children?: React.ReactNode;
	/** CSS height, e.g. "80vh" or "600px". */
	height?: string;
	/** Overlay darkness, 0-1. */
	overlayOpacity?: number;
	/** Parallax translate range in px. Higher = stronger effect. */
	strength?: number;
	className?: string;
}

const ParallaxSection = ({
	image,
	children,
	height = "80vh",
	overlayOpacity = 0.5,
	strength = 500,
	className = "",
}: Props) => {
	const ref = useRef<HTMLDivElement>(null);
	const shouldReduceMotion = useReducedMotion();

	// Scroll-linked parallax runs on the main thread, which lags badly on mobile
	// (touch scroll is composited off-thread). Only enable it on larger,
	// pointer-fine screens; everywhere else the background is static.
	const [enableParallax, setEnableParallax] = useState(false);
	useEffect(() => {
		if (shouldReduceMotion) return;
		const mq = window.matchMedia("(min-width: 1024px) and (pointer: fine)");
		const update = () => setEnableParallax(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, [shouldReduceMotion]);

	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start end", "end start"],
	});
	const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);

	return (
		<section ref={ref} className={`relative overflow-hidden w-full ${className}`} style={{ height }}>
			<motion.div
				className="absolute inset-[-10%] bg-cover bg-center"
				style={{
					backgroundImage: `url(${image})`,
					y: enableParallax ? y : 0,
					willChange: enableParallax ? "transform" : undefined,
				}}
			/>
			<div className="absolute inset-0" style={{ backgroundColor: `rgba(24, 6, 25, ${overlayOpacity})` }} />
			{children && <div className="relative z-10 h-full flex items-center justify-center px-4">{children}</div>}
		</section>
	);
};

export default ParallaxSection;
